<template>
  <!-- Modal Overlay -->
  <div v-if="isOpen" class="composer-modal-overlay" @click="handleOverlayClick">
    <div class="composer-modal" @click.stop>
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-info">
          <div class="mode-badge">
            <svg
              class="badge-icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path d="M3 7v6h6" />
              <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
            </svg>
            <span class="badge-text">Reply</span>
          </div>
          <h2 class="modal-title">Compose Reply</h2>
        </div>
        <div class="header-actions">
          <button @click="minimizeComposer" class="header-btn" title="Minimize">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          <button
            @click="closeComposer"
            class="header-btn close-btn"
            title="Close"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Modal Content -->
      <div class="modal-content">
        <div class="modal-body">
          <div class="email-composer">
            <!-- Email Form -->
            <form @submit.prevent="sendReply" class="composer-form">
              <!-- Recipients Section -->
              <div class="recipients-section">
                <!-- From Field -->
                <div class="form-row from-row">
                  <label class="form-label">From</label>
                  <div class="from-field">
                    <div class="selected-email">
                      <div
                        class="email-avatar"
                        :style="{
                          backgroundColor: getEmailColor(selectedFromEmail),
                        }"
                      >
                        {{ getEmailInitials(selectedFromEmailDisplay) }}
                      </div>
                      <div class="email-info">
                        <div class="email-address">
                          {{ selectedFromEmailDisplay }}
                        </div>
                        <div class="email-context">{{ getEmailContext() }}</div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- To Field -->
                <div class="form-row">
                  <label class="form-label">To</label>
                  <div class="recipient-field">
                    <input
                      v-model="email.recipients"
                      type="text"
                      class="form-input"
                      placeholder="recipient@example.com"
                      required
                      readonly
                    />
                  </div>
                </div>

                <!-- Subject Field -->
                <div class="form-row">
                  <label class="form-label">Subject</label>
                  <input
                    v-model="email.subject"
                    type="text"
                    class="form-input subject-input"
                    placeholder="Email subject"
                    required
                    readonly
                  />
                </div>
              </div>

              <!-- Rich Text Editor -->
              <div class="editor-section">
                <!-- Editor Toolbar -->
                <div class="editor-toolbar">
                  <div class="toolbar-group">
                    <button
                      type="button"
                      @click="execCommand('undo')"
                      class="toolbar-btn"
                      title="Undo"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M3 7v6h6" />
                        <path d="M21 17a9 9 0 0 0-9-9 9 9 0 0 0-6 2.3L3 13" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('redo')"
                      class="toolbar-btn"
                      title="Redo"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M21 7v6h-6" />
                        <path d="M3 17a9 9 0 0 1 9-9 9 9 0 0 1 6 2.3L21 13" />
                      </svg>
                    </button>
                  </div>

                  <div class="toolbar-divider"></div>

                  <div class="toolbar-group">
                    <select
                      v-model="selectedFontFamily"
                      @change="changeFontFamily"
                      class="font-select"
                    >
                      <option value="Arial, sans-serif">Arial</option>
                      <option value="Helvetica, sans-serif">Helvetica</option>
                      <option value="Georgia, serif">Georgia</option>
                      <option value="Times New Roman, serif">Times</option>
                      <option value="Courier New, monospace">Courier</option>
                    </select>

                    <select
                      v-model="selectedFontSize"
                      @change="changeFontSize"
                      class="font-select"
                    >
                      <option value="12px">12px</option>
                      <option value="14px">14px</option>
                      <option value="16px">16px</option>
                      <option value="18px">18px</option>
                      <option value="20px">20px</option>
                      <option value="24px">24px</option>
                    </select>
                  </div>

                  <div class="toolbar-divider"></div>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      @click="execCommand('bold')"
                      :class="{ active: isBold }"
                      class="toolbar-btn"
                      title="Bold"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
                        <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('italic')"
                      :class="{ active: isItalic }"
                      class="toolbar-btn"
                      title="Italic"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="19" y1="4" x2="10" y2="4" />
                        <line x1="14" y1="20" x2="5" y2="20" />
                        <line x1="15" y1="4" x2="9" y2="20" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('underline')"
                      :class="{ active: isUnderline }"
                      class="toolbar-btn"
                      title="Underline"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M6 3v7a6 6 0 0 0 6 6 6 6 0 0 0 6-6V3" />
                        <line x1="4" y1="21" x2="20" y2="21" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('strikeThrough')"
                      class="toolbar-btn"
                      title="Strikethrough"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M16 4H9a3 3 0 0 0-2.83 4" />
                        <path d="M14 12a4 4 0 0 1 0 8H6" />
                        <line x1="4" y1="12" x2="20" y2="12" />
                      </svg>
                    </button>
                  </div>

                  <div class="toolbar-divider"></div>

                  <div class="toolbar-group">
                    <input
                      type="color"
                      v-model="selectedTextColor"
                      @change="changeTextColor"
                      class="color-picker"
                      title="Text Color"
                    />
                    <input
                      type="color"
                      v-model="selectedBgColor"
                      @change="changeBackgroundColor"
                      class="color-picker"
                      title="Background Color"
                    />
                  </div>

                  <div class="toolbar-divider"></div>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      @click="execCommand('justifyLeft')"
                      class="toolbar-btn"
                      title="Align Left"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="17" y1="10" x2="3" y2="10" />
                        <line x1="21" y1="6" x2="3" y2="6" />
                        <line x1="21" y1="14" x2="3" y2="14" />
                        <line x1="17" y1="18" x2="3" y2="18" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('justifyCenter')"
                      class="toolbar-btn"
                      title="Align Center"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="18" y1="10" x2="6" y2="10" />
                        <line x1="21" y1="6" x2="3" y2="6" />
                        <line x1="21" y1="14" x2="3" y2="14" />
                        <line x1="18" y1="18" x2="6" y2="18" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('justifyRight')"
                      class="toolbar-btn"
                      title="Align Right"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="21" y1="10" x2="7" y2="10" />
                        <line x1="21" y1="6" x2="3" y2="6" />
                        <line x1="21" y1="14" x2="3" y2="14" />
                        <line x1="21" y1="18" x2="7" y2="18" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('justifyFull')"
                      class="toolbar-btn"
                      title="Justify"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="21" y1="10" x2="3" y2="10" />
                        <line x1="21" y1="6" x2="3" y2="6" />
                        <line x1="21" y1="14" x2="3" y2="14" />
                        <line x1="21" y1="18" x2="3" y2="18" />
                      </svg>
                    </button>
                  </div>

                  <div class="toolbar-divider"></div>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      @click="execCommand('insertUnorderedList')"
                      class="toolbar-btn"
                      title="Bullet List"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="8" y1="6" x2="21" y2="6" />
                        <line x1="8" y1="12" x2="21" y2="12" />
                        <line x1="8" y1="18" x2="21" y2="18" />
                        <line x1="3" y1="6" x2="3.01" y2="6" />
                        <line x1="3" y1="12" x2="3.01" y2="12" />
                        <line x1="3" y1="18" x2="3.01" y2="18" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('insertOrderedList')"
                      class="toolbar-btn"
                      title="Numbered List"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="10" y1="6" x2="21" y2="6" />
                        <line x1="10" y1="12" x2="21" y2="12" />
                        <line x1="10" y1="18" x2="21" y2="18" />
                        <path d="M4 6h1v4" />
                        <path d="M4 10h2" />
                        <path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('outdent')"
                      class="toolbar-btn"
                      title="Decrease Indent"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <polyline points="7,8 3,12 7,16" />
                        <line x1="21" y1="12" x2="11" y2="12" />
                        <line x1="21" y1="6" x2="11" y2="6" />
                        <line x1="21" y1="18" x2="11" y2="18" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="execCommand('indent')"
                      class="toolbar-btn"
                      title="Increase Indent"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <polyline points="3,8 7,12 3,16" />
                        <line x1="21" y1="12" x2="11" y2="12" />
                        <line x1="21" y1="6" x2="11" y2="6" />
                        <line x1="21" y1="18" x2="11" y2="18" />
                      </svg>
                    </button>
                  </div>

                  <div class="toolbar-divider"></div>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      @click="insertLink"
                      class="toolbar-btn"
                      title="Insert Link"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"
                        />
                        <path
                          d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="insertImage"
                      class="toolbar-btn"
                      title="Insert Image"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <rect
                          x="3"
                          y="3"
                          width="18"
                          height="18"
                          rx="2"
                          ry="2"
                        />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21,15 16,10 5,21" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      @click="insertTable"
                      class="toolbar-btn"
                      title="Insert Table"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18"
                        />
                      </svg>
                    </button>
                  </div>

                  <div class="toolbar-divider"></div>

                  <div class="toolbar-group">
                    <button
                      type="button"
                      @click="clearFormatting"
                      class="toolbar-btn"
                      title="Clear Formatting"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M6 2l3 6" />
                        <path
                          d="M23 6l-7 5 2 3h-4l-2-3H9l-1 2c-.5 1-1 2-2 2H3"
                        />
                        <path d="M7 8L3 14v4h4l4-4 .18-.18" />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- Content Editor -->
                <div class="content-editor">
                  <div
                    ref="editor"
                    class="rich-text-editor"
                    contenteditable="true"
                    @input="onContentChange"
                    @paste="onPaste"
                    @keydown="onKeyDown"
                    @focus="onEditorFocus"
                    @blur="onEditorBlur"
                    :placeholder="'Write your reply...'"
                    spellcheck="true"
                  ></div>
                </div>

                <!-- Editor Footer -->
                <div class="editor-footer">
                  <div class="editor-stats">
                    <span class="word-count">{{ wordCount }} words</span>
                    <span class="char-count">{{ charCount }} characters</span>
                  </div>
                  <div class="editor-actions">
                    <button
                      type="button"
                      @click="toggleFullscreen"
                      class="footer-btn"
                      title="Fullscreen"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Attachments Section -->
              <div v-if="attachments.length > 0" class="attachments-section">
                <h4 class="attachments-title">Attachments</h4>
                <div class="attachments-list">
                  <div
                    v-for="(attachment, index) in attachments"
                    :key="index"
                    class="attachment-item"
                  >
                    <svg
                      class="attachment-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                    >
                      <path
                        d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.48"
                      />
                    </svg>
                    <div class="attachment-info">
                      <div class="attachment-name">{{ attachment.name }}</div>
                      <div class="attachment-size">
                        {{ formatFileSize(attachment.size) }}
                      </div>
                    </div>
                    <button
                      type="button"
                      @click="removeAttachment(index)"
                      class="remove-attachment"
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Action Bar -->
              <div class="action-bar">
                <div class="action-group">
                  <input
                    ref="fileInput"
                    type="file"
                    multiple
                    @change="handleFileUpload"
                    class="file-input"
                  />
                  <button
                    type="button"
                    @click="triggerFileUpload"
                    class="action-btn"
                    title="Attach File"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66L9.64 16.2a2 2 0 0 1-2.83-2.83l8.49-8.48"
                      />
                    </svg>
                    Attach
                  </button>
                  <button
                    type="button"
                    @click="insertSignature"
                    class="action-btn"
                    title="Insert Signature"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                      />
                    </svg>
                    Signature
                  </button>
                  <button
                    type="button"
                    @click="saveAsDraft"
                    class="action-btn"
                    title="Save as Draft"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                      <path
                        d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"
                      />
                      <polyline points="17,21 17,13 7,13 7,21" />
                      <polyline points="7,3 7,8 15,8" />
                    </svg>
                    Draft
                  </button>
                </div>

                <div class="send-group">
                  <button
                    type="button"
                    @click="closeComposer"
                    class="btn btn-secondary"
                    :disabled="sending"
                  >
                    Cancel
                  </button>
                  <div class="send-options">
                    <button
                      type="submit"
                      class="btn btn-primary send-btn"
                      :disabled="sending || !canSend"
                      :title="canSend ? 'Send reply' : getDisabledReason()"
                    >
                      <svg
                        v-if="!sending"
                        class="btn-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <line x1="22" y1="2" x2="11" y2="13" />
                        <polygon points="22,2 15,22 11,13 2,9 22,2" />
                      </svg>
                      <svg
                        v-else
                        class="btn-icon spinner"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                      {{ sending ? "Sending..." : "Send" }}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <!-- Status Notification -->
      <div
        v-if="sendingStatus"
        class="status-notification"
        :class="`status-${sendingStatus.type}`"
      >
        <div class="status-content">
          <svg
            v-if="sendingStatus.type === 'info'"
            class="status-icon spinner"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="12" cy="12" r="10" />
            <path d="M12 6v6l4 2" />
          </svg>
          <svg
            v-else-if="sendingStatus.type === 'success'"
            class="status-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <polyline points="20,6 9,17 4,12" />
          </svg>
          <svg
            v-else-if="sendingStatus.type === 'error'"
            class="status-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span class="status-message">{{ sendingStatus.message }}</span>
          <button @click="sendingStatus = null" class="status-close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useStore } from "vuex";

export default {
  name: "EmailComposer",
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
    originalMessage: {
      type: Object,
      default: () => null,
    },
  },
  emits: ["close", "sent", "minimize"],
  setup(props, { emit }) {
    const store = useStore();

    // Refs
    const editor = ref(null);
    const fileInput = ref(null);

    // State
    const email = ref({
      email_address_id: "",
      recipients: "",
      subject: "",
      content: "",
      html_content: "",
    });

    const attachments = ref([]);
    const sending = ref(false);
    const sendingStatus = ref(null);
    const showSendOptions = ref(false);
    const isFullscreen = ref(false);

    // Rich text editor state
    const selectedFontFamily = ref("Arial, sans-serif");
    const selectedFontSize = ref("14px");
    const selectedTextColor = ref("#000000");
    const selectedBgColor = ref("#ffffff");
    const isBold = ref(false);
    const isItalic = ref(false);
    const isUnderline = ref(false);
    const wordCount = ref(0);
    const charCount = ref(0);

    // Computed properties
    const emailTemplates = computed(() => store.getters.emailTemplates);
    const userEmailAddresses = computed(() => store.getters.userEmailAddresses);

    const canSend = computed(() => {
      return (
        email.value.email_address_id &&
        email.value.recipients &&
        email.value.subject &&
        email.value.content.trim()
      );
    });

    const selectedFromEmail = computed(() => {
      if (props.originalMessage?.email_address?.email) {
        return props.originalMessage.email_address.email;
      }
      return "user@example.com";
    });

    const selectedFromEmailDisplay = computed(() => {
      if (props.originalMessage?.email_address?.display_name) {
        return props.originalMessage.email_address.display_name;
      }
      return selectedFromEmail.value;
    });

    // Rich text editor methods
    const execCommand = (command, value = null) => {
      document.execCommand(command, false, value);
      updateFormatStates();
      onContentChange();
    };

    const changeFontFamily = () => {
      execCommand("fontName", selectedFontFamily.value);
    };

    const changeFontSize = () => {
      execCommand("fontSize", "3");
      // Apply custom font size via CSS
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        const span = document.createElement("span");
        span.style.fontSize = selectedFontSize.value;
        try {
          range.surroundContents(span);
        } catch (e) {
          // Handle case where range spans multiple elements
          span.appendChild(range.extractContents());
          range.insertNode(span);
        }
      }
      onContentChange();
    };

    const changeTextColor = () => {
      execCommand("foreColor", selectedTextColor.value);
    };

    const changeBackgroundColor = () => {
      execCommand("backColor", selectedBgColor.value);
    };

    const insertLink = () => {
      const url = prompt("Enter the URL:");
      if (url) {
        execCommand("createLink", url);
      }
    };

    const insertImage = () => {
      const url = prompt("Enter the image URL:");
      if (url) {
        execCommand("insertImage", url);
      }
    };

    const insertTable = () => {
      const rows = prompt("Number of rows:", "3");
      const cols = prompt("Number of columns:", "3");
      if (rows && cols) {
        let tableHTML = "<table border='1' cellpadding='5' cellspacing='0'>";
        for (let i = 0; i < parseInt(rows); i++) {
          tableHTML += "<tr>";
          for (let j = 0; j < parseInt(cols); j++) {
            tableHTML += "<td>&nbsp;</td>";
          }
          tableHTML += "</tr>";
        }
        tableHTML += "</table>";
        execCommand("insertHTML", tableHTML);
      }
    };

    const clearFormatting = () => {
      execCommand("removeFormat");
      execCommand("unlink");
    };

    const updateFormatStates = () => {
      try {
        isBold.value = document.queryCommandState("bold");
        isItalic.value = document.queryCommandState("italic");
        isUnderline.value = document.queryCommandState("underline");
      } catch (e) {
        // Ignore errors
      }
    };

    const updateWordCount = () => {
      if (editor.value) {
        const text = editor.value.innerText || "";
        wordCount.value = text.trim() ? text.trim().split(/\s+/).length : 0;
        charCount.value = text.length;
      }
    };

    // Event handlers
    const onContentChange = () => {
      if (editor.value) {
        email.value.html_content = editor.value.innerHTML;
        email.value.content = editor.value.innerText || "";
        updateWordCount();
      }
    };

    const onPaste = (event) => {
      event.preventDefault();
      const text = event.clipboardData.getData("text/plain");
      execCommand("insertText", text);
    };

    const onKeyDown = (event) => {
      // Handle keyboard shortcuts
      if (event.ctrlKey || event.metaKey) {
        switch (event.key.toLowerCase()) {
          case "b":
            event.preventDefault();
            execCommand("bold");
            break;
          case "i":
            event.preventDefault();
            execCommand("italic");
            break;
          case "u":
            event.preventDefault();
            execCommand("underline");
            break;
          case "z":
            if (event.shiftKey) {
              event.preventDefault();
              execCommand("redo");
            } else {
              event.preventDefault();
              execCommand("undo");
            }
            break;
        }
      }

      // Update format states after any key press
      setTimeout(updateFormatStates, 10);
    };

    const onEditorFocus = () => {
      updateFormatStates();
    };

    const onEditorBlur = () => {
      // Save draft when user stops editing
      setTimeout(() => {
        if (email.value.content.trim()) {
          saveAsDraft();
        }
      }, 1000);
    };

    // File handling
    const triggerFileUpload = () => {
      fileInput.value?.click();
    };

    const handleFileUpload = (event) => {
      const files = Array.from(event.target.files);
      files.forEach((file) => {
        // Validate file size (optional - adjust limit as needed)
        const maxSize = 25 * 1024 * 1024; // 25MB
        if (file.size > maxSize) {
          alert(`File "${file.name}" is too large. Maximum size is 25MB.`);
          return;
        }

        attachments.value.push({
          name: file.name,
          size: file.size,
          file: file, // Store the actual File object
          type: file.type,
        });
      });
      event.target.value = ""; // Reset input
    };

    const removeAttachment = (index) => {
      attachments.value.splice(index, 1);
    };

    const formatFileSize = (bytes) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    // Action methods
    const insertSignature = () => {
      // Get the display name or fallback to email
      const senderName =
        props.originalMessage?.email_address?.display_name ||
        props.originalMessage?.email_address?.full_name ||
        selectedFromEmail.value;

      const signature = `
        <br><br>
        <div style="border-top: 1px solid #e2e8f0; padding-top: 1rem; margin-top: 1rem;">
          <p style="margin: 0; color: #64748b; font-size: 0.875rem;">
            Best regards,<br>
            ${senderName}
          </p>
        </div>
      `;
      execCommand("insertHTML", signature);
    };

    const saveAsDraft = async () => {
      try {
        await store.dispatch("saveDraft", {
          ...email.value,
          attachments: attachments.value,
        });

        sendingStatus.value = {
          type: "success",
          message: "Draft saved successfully",
        };

        setTimeout(() => {
          sendingStatus.value = null;
        }, 2000);
      } catch (error) {
        console.error("Failed to save draft:", error);
      }
    };

    const toggleFullscreen = () => {
      isFullscreen.value = !isFullscreen.value;
      // Implementation for fullscreen mode
    };

    const scheduleEmail = () => {
      // Implementation for email scheduling
      alert("Email scheduling feature coming soon!");
    };

    const sendWithConfirmation = () => {
      // Implementation for send with confirmation
      alert("Send with confirmation feature coming soon!");
    };

    // Core email methods
    const loadUserEmailAddresses = async () => {
      // Check if user is authenticated before making the request
      if (!store.getters.isAuthenticated) {
        console.warn("Cannot load email addresses: user not authenticated");
        email.value.email_address_id = 1;
        return;
      }

      try {
        await store.dispatch("fetchUserEmailAddresses");

        if (userEmailAddresses.value && userEmailAddresses.value.length > 0) {
          if (props.originalMessage) {
            const originalEmailId =
              props.originalMessage.email_address?.id ||
              props.originalMessage.email_address_id ||
              props.originalMessage.email_address;

            if (originalEmailId) {
              email.value.email_address_id = originalEmailId;
            } else {
              email.value.email_address_id = userEmailAddresses.value[0].id;
            }
          } else {
            email.value.email_address_id = userEmailAddresses.value[0].id;
          }
        } else {
          email.value.email_address_id = 1;
        }
      } catch (error) {
        console.error("Failed to load email addresses:", error);
        if (!email.value.email_address_id) {
          email.value.email_address_id = 1;
        }
      }
    };

    const sendReply = async () => {
      if (!canSend.value || sending.value) {
        return;
      }

      sending.value = true;
      sendingStatus.value = {
        type: "info",
        message: "Sending reply...",
      };

      try {
        // Prepare the reply data
        const replyData = {
          email_address_id: email.value.email_address_id,
          recipients: email.value.recipients,
          subject: email.value.subject,
          content: email.value.content,
          html_content: email.value.html_content,
        };

        // Add attachments as File objects for FormData
        if (attachments.value.length > 0) {
          replyData.attachments = attachments.value.map(
            (attachment) => attachment.file
          );
        }

        const response = await store.dispatch("replyToEmail", {
          messageId: props.originalMessage.id,
          replyData: replyData,
        });

        if (response?.data?.id || response?.id) {
          const messageId = response.data?.id || response.id;
          await store.dispatch("sendMessage", messageId);

          sendingStatus.value = {
            type: "success",
            message: "Reply sent successfully!",
          };

          setTimeout(() => {
            emit("sent", response.data || response);
            closeComposer();
          }, 2000);
        } else {
          throw new Error("Failed to create reply - no ID returned");
        }
      } catch (error) {
        console.error("Failed to send reply:", error);

        let errorMessage = "Failed to send reply";

        // Handle specific error cases
        if (error.response?.status === 400) {
          if (error.response.data?.error) {
            errorMessage = error.response.data.error;
          } else if (error.response.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data?.detail) {
            errorMessage = error.response.data.detail;
          } else {
            // Try to extract field-specific errors
            const data = error.response.data;
            if (typeof data === "object") {
              const fieldErrors = [];
              Object.keys(data).forEach((field) => {
                if (Array.isArray(data[field])) {
                  fieldErrors.push(`${field}: ${data[field].join(", ")}`);
                } else {
                  fieldErrors.push(`${field}: ${data[field]}`);
                }
              });
              if (fieldErrors.length > 0) {
                errorMessage = fieldErrors.join("; ");
              }
            }
          }
        } else if (error.response?.status === 413) {
          errorMessage =
            "Attachments are too large. Please reduce file sizes and try again.";
        } else if (error.response?.status === 422) {
          errorMessage =
            "Invalid attachment format. Please check your files and try again.";
        } else if (error.message) {
          errorMessage = error.message;
        }

        sendingStatus.value = {
          type: "error",
          message: errorMessage,
        };

        setTimeout(() => {
          sendingStatus.value = null;
        }, 5000);
      } finally {
        sending.value = false;
      }
    };

    const setupReply = () => {
      if (!props.originalMessage) {
        console.warn("No original message provided for reply");
        return;
      }

      // Default recipient
      let recipient = props.originalMessage.sender || "";

      // For conversations, always reply to the other participant (not the current user)
      if (
        props.originalMessage.conversation?.participants_list &&
        Array.isArray(props.originalMessage.conversation.participants_list) &&
        props.originalMessage.conversation.participants_list.length >= 2
      ) {
        const conversationParticipants =
          props.originalMessage.conversation.participants_list;

        // Get the current user's email address(es)
        const currentUserEmails =
          userEmailAddresses.value?.map((addr) => addr.email) || [];

        if (currentUserEmails.length > 0) {
          // Find the participant who is NOT the current user
          const otherParticipant = conversationParticipants.find(
            (participant) => !currentUserEmails.includes(participant)
          );

          if (otherParticipant) {
            recipient = otherParticipant;
          }
        }
      }
      // If we have lastReceivedMessage data, prefer its sender
      else if (props.originalMessage.lastReceivedMessage) {
        recipient = props.originalMessage.lastReceivedMessage.sender;
      }

      email.value.recipients = recipient;
      email.value.subject = props.originalMessage.subject?.startsWith("Re:")
        ? props.originalMessage.subject
        : `Re: ${props.originalMessage.subject || "No Subject"}`;

      if (
        !email.value.email_address_id &&
        userEmailAddresses.value?.length > 0
      ) {
        const originalEmailId =
          props.originalMessage.email_address?.id ||
          props.originalMessage.email_address_id ||
          props.originalMessage.email_address;

        if (originalEmailId) {
          email.value.email_address_id = originalEmailId;
        } else {
          email.value.email_address_id = userEmailAddresses.value[0].id;
        }
      }

      // Set focus to editor after setup
      nextTick(() => {
        if (editor.value) {
          editor.value.focus();
        }
      });
    };

    // Helper methods
    const getEmailColor = (emailAddress) => {
      if (!emailAddress) return "#3b82f6";

      const colors = [
        "#3b82f6",
        "#8b5cf6",
        "#ec4899",
        "#f97316",
        "#14b8a6",
        "#f59e0b",
        "#10b981",
        "#6366f1",
        "#ef4444",
        "#84cc16",
      ];

      let hash = 0;
      for (let i = 0; i < emailAddress.length; i++) {
        hash = emailAddress.charCodeAt(i) + ((hash << 5) - hash);
      }

      return colors[Math.abs(hash) % colors.length];
    };

    const getEmailInitials = (emailOrName) => {
      if (!emailOrName) return "??";

      // If it looks like a name (contains spaces), use name initials
      if (emailOrName.includes(" ")) {
        const nameParts = emailOrName.trim().split(" ");
        if (nameParts.length >= 2) {
          return (
            nameParts[0].charAt(0) + nameParts[nameParts.length - 1].charAt(0)
          ).toUpperCase();
        }
        return nameParts[0].slice(0, 2).toUpperCase();
      }

      // Otherwise use email logic
      const localPart = emailOrName.split("@")[0];
      return localPart.slice(0, 2).toUpperCase();
    };

    const getEmailContext = () => {
      return "Replying from this inbox";
    };

    const getDisabledReason = () => {
      if (sending.value) return "Sending in progress...";
      if (!email.value.email_address_id) return "Email address not configured";
      if (!email.value.recipients) return "No recipient specified";
      if (!email.value.subject) return "No subject specified";
      if (!email.value.content.trim()) return "Please enter your reply";
      return "Ready to send";
    };

    // Modal controls
    const closeComposer = () => {
      showSendOptions.value = false;
      emit("close");
    };

    const minimizeComposer = () => {
      emit("minimize");
    };

    const handleOverlayClick = () => {
      closeComposer();
    };

    const handleEscKey = (event) => {
      if (event.key === "Escape" && props.isOpen) {
        if (showSendOptions.value) {
          showSendOptions.value = false;
        } else {
          closeComposer();
        }
      }
    };

    // Lifecycle
    onMounted(async () => {
      document.addEventListener("keydown", handleEscKey);
      if (props.isOpen) {
        document.body.style.overflow = "hidden";
      }

      await loadUserEmailAddresses();
      setupReply();
    });

    onUnmounted(() => {
      document.removeEventListener("keydown", handleEscKey);
      document.body.style.overflow = "";
    });

    // Watchers
    watch(
      () => props.isOpen,
      async (newValue) => {
        if (newValue) {
          document.body.style.overflow = "hidden";
          await loadUserEmailAddresses();
          setupReply();
        } else {
          document.body.style.overflow = "";
          showSendOptions.value = false;
        }
      }
    );

    watch(
      () => props.originalMessage,
      async () => {
        if (props.originalMessage) {
          await loadUserEmailAddresses();
          setupReply();
        }
      }
    );

    return {
      // Refs
      editor,
      fileInput,

      // State
      email,
      attachments,
      sending,
      sendingStatus,
      showSendOptions,
      isFullscreen,

      // Rich text editor state
      selectedFontFamily,
      selectedFontSize,
      selectedTextColor,
      selectedBgColor,
      isBold,
      isItalic,
      isUnderline,
      wordCount,
      charCount,

      // Computed
      userEmailAddresses,
      emailTemplates,
      canSend,
      selectedFromEmail,
      selectedFromEmailDisplay,

      // Rich text methods
      execCommand,
      changeFontFamily,
      changeFontSize,
      changeTextColor,
      changeBackgroundColor,
      insertLink,
      insertImage,
      insertTable,
      clearFormatting,
      updateFormatStates,

      // Event handlers
      onContentChange,
      onPaste,
      onKeyDown,
      onEditorFocus,
      onEditorBlur,

      // File handling
      triggerFileUpload,
      handleFileUpload,
      removeAttachment,
      formatFileSize,

      // Actions
      insertSignature,
      saveAsDraft,
      toggleFullscreen,
      scheduleEmail,
      sendWithConfirmation,

      // Core methods
      sendReply,
      closeComposer,
      minimizeComposer,
      handleOverlayClick,

      // Helpers
      getEmailColor,
      getEmailInitials,
      getEmailContext,
      getDisabledReason,
    };
  },
};
</script>

<style lang="scss" scoped>
// SCSS Variables
$primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
$success-gradient: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
$error-gradient: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
$warning-gradient: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);

$colors: (
  primary: #667eea,
  primary-dark: #5a67d8,
  primary-light: #e2e8f0,
  success: #48bb78,
  success-light: #9ae6b4,
  error: #f56565,
  error-light: #fed7d7,
  warning: #ed8936,
  warning-light: #fbd38d,
  text-primary: #2d3748,
  text-secondary: #4a5568,
  text-muted: #718096,
  text-light: #a0aec0,
  bg-white: #ffffff,
  bg-light: #f7fafc,
  bg-gray: #edf2f7,
  bg-dark: #2d3748,
  border: #e2e8f0,
  border-focus: #667eea,
  shadow: rgba(0, 0, 0, 0.1),
);

$shadows: (
  sm: (
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06),
  ),
  md: (
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06),
  ),
  lg: (
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05),
  ),
  xl: (
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04),
  ),
  2xl: 0 25px 50px -12px rgba(0, 0, 0, 0.25),
);

$border-radius: (
  sm: 0.375rem,
  md: 0.5rem,
  lg: 0.75rem,
  xl: 1rem,
  full: 9999px,
);

$transitions: (
  fast: all 0.15s cubic-bezier(0.4, 0, 0.2, 1),
  normal: all 0.3s cubic-bezier(0.4, 0, 0.2, 1),
  slow: all 0.5s cubic-bezier(0.4, 0, 0.2, 1),
);

// Mixins
@mixin flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
}

@mixin glass-effect {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

@mixin hover-lift {
  transition: map-get($transitions, fast);

  &:hover {
    transform: translateY(-2px);
    box-shadow: map-get($shadows, lg);
  }
}

@mixin button-style($bg-color, $text-color: #fff) {
  @include flex-center;
  padding: 0.75rem 1.5rem;
  border-radius: map-get($border-radius, md);
  font-weight: 600;
  font-size: 0.875rem;
  background: $bg-color;
  color: $text-color;
  border: none;
  cursor: pointer;
  transition: map-get($transitions, fast);
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.1);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
  }
}

// Modal Overlay
.composer-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
  @include flex-center;
  z-index: 1000;
  padding: 2rem;
  animation: fadeIn 0.3s ease-out;
}

// Modal Container
.composer-modal {
  @include glass-effect;
  border-radius: map-get($border-radius, xl);
  box-shadow: map-get($shadows, 2xl);
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

// Modal Header
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  background: $primary-gradient;
  color: white;
  border-radius: map-get($border-radius, xl) map-get($border-radius, xl) 0 0;

  .header-info {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;
    min-width: 0;
  }

  .mode-badge {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border-radius: map-get($border-radius, full);
    background: rgba(255, 255, 255, 0.2);
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.3);

    .badge-icon {
      width: 0.875rem;
      height: 0.875rem;
      stroke-width: 2.5;
    }
  }

  .modal-title {
    font-size: 1.375rem;
    font-weight: 700;
    margin: 0;
    background: linear-gradient(
      135deg,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0.8)
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .header-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .header-btn {
    @include flex-center;
    width: 2.5rem;
    height: 2.5rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: map-get($border-radius, md);
    color: rgba(255, 255, 255, 0.9);
    cursor: pointer;
    transition: map-get($transitions, fast);
    backdrop-filter: blur(10px);

    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-1px);
    }

    &.close-btn:hover {
      background: rgba(245, 101, 101, 0.2);
      border-color: rgba(245, 101, 101, 0.3);
    }

    svg {
      width: 1.125rem;
      height: 1.125rem;
      stroke-width: 2;
    }
  }
}

// Modal Content
.modal-content {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: map-get($colors, bg-white);
}

// Modal Body - Scrollable container
.modal-body {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  // Custom scrollbar styling
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: map-get($colors, bg-light);
    border-radius: map-get($border-radius, sm);
  }

  &::-webkit-scrollbar-thumb {
    background: map-get($colors, text-light);
    border-radius: map-get($border-radius, sm);

    &:hover {
      background: map-get($colors, text-muted);
    }
  }
}

// Email Composer
.email-composer {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.composer-form {
  flex: 1;
  display: flex;
  flex-direction: column;
}

// Recipients Section
.recipients-section {
  background: linear-gradient(
    135deg,
    map-get($colors, bg-light),
    map-get($colors, bg-white)
  );
  border-bottom: 2px solid map-get($colors, border);

  .form-row {
    display: flex;
    align-items: center;
    padding: 1rem 2rem;
    border-bottom: 1px solid rgba(226, 232, 240, 0.5);

    &:last-child {
      border-bottom: none;
    }

    &.from-row {
      background: rgba(102, 126, 234, 0.05);
    }
  }

  .form-label {
    font-size: 0.875rem;
    font-weight: 700;
    color: map-get($colors, text-primary);
    min-width: 5rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-right: 1rem;
  }

  .form-input {
    flex: 1;
    padding: 0.875rem 1rem;
    border: 2px solid map-get($colors, border);
    border-radius: map-get($border-radius, md);
    font-size: 0.875rem;
    font-weight: 500;
    transition: map-get($transitions, fast);
    background: map-get($colors, bg-white);

    &:focus {
      outline: none;
      border-color: map-get($colors, border-focus);
      box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
    }

    &[readonly] {
      background: map-get($colors, bg-light);
      color: map-get($colors, text-secondary);
    }

    &.subject-input {
      font-weight: 600;
    }
  }

  .from-field {
    flex: 1;
  }

  .selected-email {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: map-get($colors, bg-white);
    border: 2px solid map-get($colors, border);
    border-radius: map-get($border-radius, md);
    box-shadow: map-get($shadows, sm);
  }

  .email-avatar {
    @include flex-center;
    width: 3rem;
    height: 3rem;
    border-radius: map-get($border-radius, full);
    color: white;
    font-size: 0.875rem;
    font-weight: 700;
    box-shadow: map-get($shadows, md);
    border: 3px solid rgba(255, 255, 255, 0.8);
  }

  .email-info {
    flex: 1;

    .email-address {
      font-size: 0.875rem;
      font-weight: 600;
      color: map-get($colors, text-primary);
    }

    .email-context {
      font-size: 0.75rem;
      color: map-get($colors, text-muted);
      font-weight: 500;
    }
  }

  .cc-bcc-controls {
    display: flex;
    gap: 0.5rem;
    margin-left: auto;

    .control-btn {
      @include button-style(transparent, map-get($colors, text-secondary));
      padding: 0.5rem 0.75rem;
      font-size: 0.75rem;
      border: 1px solid map-get($colors, border);

      &.active {
        background: map-get($colors, primary);
        color: white;
        border-color: map-get($colors, primary);
      }
    }
  }
}

// Editor Section
.editor-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: map-get($colors, bg-white);

  .editor-toolbar {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 2rem;
    background: linear-gradient(135deg, #f8fafc, #e2e8f0);
    border-bottom: 2px solid map-get($colors, border);
    overflow-x: auto;

    .toolbar-group {
      display: flex;
      align-items: center;
      gap: 0.25rem;

      .toolbar-btn {
        @include flex-center;
        width: 2.25rem;
        height: 2.25rem;
        background: map-get($colors, bg-white);
        border: 1px solid map-get($colors, border);
        border-radius: map-get($border-radius, sm);
        color: map-get($colors, text-muted);
        cursor: pointer;
        transition: map-get($transitions, fast);
        box-shadow: map-get($shadows, sm);

        &:hover {
          background: map-get($colors, primary-light);
          color: map-get($colors, primary);
          border-color: map-get($colors, primary);
          @include hover-lift;
        }

        &.active {
          background: map-get($colors, primary);
          color: white;
          border-color: map-get($colors, primary);
          box-shadow: map-get($shadows, md);
        }

        svg {
          width: 1rem;
          height: 1rem;
          stroke-width: 2;
        }
      }

      .font-select {
        padding: 0.5rem 0.75rem;
        border: 1px solid map-get($colors, border);
        border-radius: map-get($border-radius, sm);
        font-size: 0.75rem;
        background: map-get($colors, bg-white);
        min-width: 80px;

        &:focus {
          outline: none;
          border-color: map-get($colors, border-focus);
        }
      }

      .color-picker {
        width: 2.25rem;
        height: 2.25rem;
        border: 1px solid map-get($colors, border);
        border-radius: map-get($border-radius, sm);
        cursor: pointer;
        background: none;

        &::-webkit-color-swatch-wrapper {
          padding: 0;
        }

        &::-webkit-color-swatch {
          border: none;
          border-radius: map-get($border-radius, sm);
        }
      }
    }

    .toolbar-divider {
      width: 1px;
      height: 1.5rem;
      background: map-get($colors, border);
      margin: 0 0.25rem;
    }
  }

  .content-editor {
    flex: 1;
    display: flex;
    flex-direction: column;
    position: relative;

    .rich-text-editor {
      flex: 1;
      padding: 2rem;
      border: none;
      outline: none;
      font-family: inherit;
      font-size: 0.875rem;
      line-height: 1.6;
      color: map-get($colors, text-primary);
      min-height: 300px;
      overflow-y: auto;
      background: map-get($colors, bg-white);

      &:empty::before {
        content: attr(placeholder);
        color: map-get($colors, text-light);
        font-style: italic;
      }

      &:focus {
        background: rgba(102, 126, 234, 0.02);
      }

      // Rich text content styling
      p {
        margin: 0 0 1rem 0;

        &:last-child {
          margin-bottom: 0;
        }
      }

      ul,
      ol {
        padding-left: 1.5rem;
        margin: 0 0 1rem 0;
      }

      blockquote {
        border-left: 4px solid map-get($colors, primary);
        padding-left: 1rem;
        margin: 1rem 0;
        font-style: italic;
        color: map-get($colors, text-secondary);
      }

      table {
        border-collapse: collapse;
        width: 100%;
        margin: 1rem 0;

        td,
        th {
          border: 1px solid map-get($colors, border);
          padding: 0.5rem;
          text-align: left;
        }

        th {
          background: map-get($colors, bg-light);
          font-weight: 600;
        }
      }

      a {
        color: map-get($colors, primary);
        text-decoration: underline;

        &:hover {
          color: map-get($colors, primary-dark);
        }
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: map-get($border-radius, sm);
      }
    }
  }

  .editor-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 2rem;
    background: map-get($colors, bg-light);
    border-top: 1px solid map-get($colors, border);

    .editor-stats {
      display: flex;
      gap: 1rem;
      font-size: 0.75rem;
      color: map-get($colors, text-muted);

      span {
        font-weight: 500;
      }
    }

    .editor-actions {
      .footer-btn {
        @include flex-center;
        width: 2rem;
        height: 2rem;
        background: transparent;
        border: 1px solid map-get($colors, border);
        border-radius: map-get($border-radius, sm);
        color: map-get($colors, text-muted);
        cursor: pointer;
        transition: map-get($transitions, fast);

        &:hover {
          color: map-get($colors, primary);
          border-color: map-get($colors, primary);
        }

        svg {
          width: 0.875rem;
          height: 0.875rem;
        }
      }
    }
  }
}

// Attachments Section
.attachments-section {
  padding: 1.5rem 2rem;
  background: map-get($colors, bg-light);
  border-bottom: 1px solid map-get($colors, border);
  max-height: 200px;
  overflow-y: auto;

  // Custom scrollbar for attachments
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: map-get($colors, text-light);
    border-radius: map-get($border-radius, sm);

    &:hover {
      background: map-get($colors, text-muted);
    }
  }

  .attachments-title {
    font-size: 0.875rem;
    font-weight: 700;
    color: map-get($colors, text-primary);
    margin: 0 0 1rem 0;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    position: sticky;
    top: 0;
    background: map-get($colors, bg-light);
    z-index: 1;
    padding-bottom: 0.5rem;
  }

  .attachments-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .attachment-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    background: map-get($colors, bg-white);
    border: 1px solid map-get($colors, border);
    border-radius: map-get($border-radius, md);
    box-shadow: map-get($shadows, sm);
    transition: map-get($transitions, fast);

    &:hover {
      border-color: map-get($colors, primary);
      box-shadow: map-get($shadows, md);
    }

    .attachment-icon {
      width: 1.125rem;
      height: 1.125rem;
      color: map-get($colors, primary);
      flex-shrink: 0;
    }

    .attachment-info {
      flex: 1;
      min-width: 0;

      .attachment-name {
        font-size: 0.875rem;
        font-weight: 600;
        color: map-get($colors, text-primary);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        margin-bottom: 0.25rem;
      }

      .attachment-size {
        font-size: 0.75rem;
        color: map-get($colors, text-muted);
        font-weight: 500;
      }
    }

    .remove-attachment {
      @include flex-center;
      width: 1.5rem;
      height: 1.5rem;
      background: transparent;
      border: none;
      color: map-get($colors, text-muted);
      cursor: pointer;
      border-radius: map-get($border-radius, sm);
      transition: map-get($transitions, fast);
      flex-shrink: 0;

      &:hover {
        color: map-get($colors, error);
        background: rgba(245, 101, 101, 0.1);
        transform: scale(1.1);
      }

      svg {
        width: 0.875rem;
        height: 0.875rem;
        stroke-width: 2.5;
      }
    }
  }
}

// Action Bar
.action-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, #f8fafc, #ffffff);
  border-top: 2px solid map-get($colors, border);

  .action-group {
    display: flex;
    align-items: center;
    gap: 0.75rem;

    .file-input {
      display: none;
    }

    .action-btn {
      @include button-style(transparent, map-get($colors, text-secondary));
      gap: 0.5rem;
      padding: 0.625rem 1rem;
      border: 1px solid map-get($colors, border);
      font-size: 0.75rem;

      &:hover {
        background: map-get($colors, bg-light);
        border-color: map-get($colors, primary);
        color: map-get($colors, primary);
      }

      svg {
        width: 0.875rem;
        height: 0.875rem;
      }
    }
  }

  .send-group {
    display: flex;
    align-items: center;
    gap: 1rem;

    .btn {
      @include flex-center;
      gap: 0.5rem;
      padding: 0.875rem 1.5rem;
      border-radius: map-get($border-radius, md);
      font-size: 0.875rem;
      font-weight: 600;
      border: 2px solid transparent;
      cursor: pointer;
      transition: map-get($transitions, fast);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      box-shadow: map-get($shadows, sm);

      &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2);
      }

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none !important;
      }

      .btn-icon {
        width: 1rem;
        height: 1rem;
        stroke-width: 2.5;
      }

      &.btn-primary {
        background: $success-gradient;
        color: white;
        border-color: map-get($colors, success);
        font-weight: 700;
        min-width: 140px;

        &:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: map-get($shadows, lg);
          filter: brightness(1.1);
        }
      }

      &.btn-secondary {
        background: map-get($colors, bg-white);
        color: map-get($colors, text-secondary);
        border-color: map-get($colors, border);

        &:hover:not(:disabled) {
          background: map-get($colors, bg-light);
          color: map-get($colors, text-primary);
          transform: translateY(-1px);
        }
      }
    }

    .send-options {
      position: relative;
      display: flex;
    }
  }
}

// Status Notification
.status-notification {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20;

  .status-content {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.5rem;
    border-radius: map-get($border-radius, lg);
    font-size: 0.875rem;
    font-weight: 600;
    box-shadow: map-get($shadows, xl);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);

    .status-icon {
      width: 1.25rem;
      height: 1.25rem;
      stroke-width: 2.5;
      flex-shrink: 0;
    }

    .status-message {
      flex: 1;
      white-space: nowrap;
    }

    .status-close {
      @include flex-center;
      width: 1.5rem;
      height: 1.5rem;
      background: transparent;
      border: none;
      border-radius: map-get($border-radius, sm);
      cursor: pointer;
      opacity: 0.7;

      &:hover {
        opacity: 1;
        background: rgba(255, 255, 255, 0.1);
      }

      svg {
        width: 0.875rem;
        height: 0.875rem;
      }
    }
  }

  &.status-info .status-content {
    background: rgba(102, 126, 234, 0.95);
    color: white;
  }

  &.status-success .status-content {
    background: rgba(72, 187, 120, 0.95);
    color: white;
  }

  &.status-error .status-content {
    background: rgba(245, 101, 101, 0.95);
    color: white;
  }
}

// Animations
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.spinner {
  animation: spin 1s linear infinite;
}

// Responsive Design
@media (max-width: 1024px) {
  .composer-modal {
    max-width: 95vw;
    margin: 1rem;
  }

  .editor-toolbar {
    .toolbar-group {
      gap: 0.125rem;

      .toolbar-btn {
        width: 2rem;
        height: 2rem;
      }
    }
  }
}

@media (max-width: 768px) {
  .composer-modal-overlay {
    padding: 1rem;
  }

  .composer-modal {
    max-height: 95vh;
    border-radius: map-get($border-radius, lg);
  }

  .modal-header {
    padding: 1rem 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;

    .header-info {
      width: 100%;
      justify-content: space-between;
    }

    .header-actions {
      width: 100%;
      justify-content: flex-end;
    }

    .modal-title {
      font-size: 1.125rem;
    }
  }

  .recipients-section .form-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;

    .form-label {
      min-width: auto;
      margin-right: 0;
    }

    .form-input,
    .selected-email {
      width: 100%;
    }
  }

  .editor-toolbar {
    padding: 0.75rem 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;

    .toolbar-group {
      flex-wrap: wrap;
    }
  }

  .action-bar {
    flex-direction: column;
    gap: 1rem;

    .action-group,
    .send-group {
      width: 100%;
      justify-content: center;
    }
  }
}

@media (max-width: 480px) {
  .composer-modal-overlay {
    padding: 0.5rem;
  }

  .composer-modal {
    border-radius: map-get($border-radius, md);
    max-height: 98vh;
  }

  .modal-header {
    padding: 0.75rem 1rem;
  }

  .mode-badge {
    padding: 0.375rem 0.75rem;
    font-size: 0.625rem;
  }

  .badge-icon {
    width: 0.75rem;
    height: 0.75rem;
  }

  .header-btn {
    width: 2rem;
    height: 2rem;
  }

  .header-btn svg {
    width: 1rem;
    height: 1rem;
  }
}

// Dark mode support (optional)
@media (prefers-color-scheme: dark) {
  .composer-modal {
    background: rgba(45, 55, 72, 0.95);
    color: #e2e8f0;

    .recipients-section {
      background: linear-gradient(135deg, #2d3748, #4a5568);
    }

    .form-input {
      background: #4a5568;
      border-color: #718096;
      color: #e2e8f0;

      &:focus {
        border-color: map-get($colors, primary);
      }
    }

    .rich-text-editor {
      background: #4a5568;
      color: #e2e8f0;
    }

    .editor-toolbar {
      background: linear-gradient(135deg, #2d3748, #4a5568);

      .toolbar-btn {
        background: #4a5568;
        border-color: #718096;
        color: #a0aec0;

        &:hover {
          background: #667eea;
          color: white;
        }
      }
    }
  }
}
</style>
